import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { LocalStorageService } from '../local-storage.service';
import { TranslateService } from '@ngx-translate/core';
import { TranslationService } from '../translation.service';
import { ThemeService } from '../service/theme.service';
import { filter } from 'rxjs/operators'

@Component({
    selector: 'app-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.css'],
    standalone: false
})
export class FooterComponent implements OnInit {

  // Variable to store the selected value - starts with default value
  selectedLanguage: string = "fr";

  // Retrieve current language selected from local storage
  initLang: string = this.localStorageService.getItem('language') ?? 'fr';

  public currentYear: number = 0;

  options: string[] = ['FR', 'EN', 'ES'];
  isOpen: boolean = false;

  public isMouseInteraction: boolean = false;
  public isSelectOpen: boolean = false;

  public isMobile: boolean = false;

  public isLoginPage: boolean = false;
  public isManagePage: boolean = false;

  public languages = [
    { label: 'FR', value: 'fr' },
    { label: 'EN', value: 'en' },
    { label: 'ES', value: 'es' }
  ];

  // Emit event when mode changes
  @Output() mode = new EventEmitter<boolean>();

  constructor(
    private languageService: TranslateService,
    private localStorageService: LocalStorageService,
    private translate: TranslationService,
    public theme: ThemeService,
    private router: Router
  ) {
    // check the language defined from localstorage and insert as current option in Select Language Menu
    switch(this.initLang) {
      case 'fr':
        this.selectedLanguage = 'fr'; // french
        break;
      case 'en':
        this.selectedLanguage = 'en'; // english
        break;
      case 'es':
        this.selectedLanguage = 'es'; // espanish
        break;
      default:
        this.selectedLanguage = 'fr'; // french
        break;
    }
   }

  // Lifecycle hook called when the component is initialized
  ngOnInit(): void{
    // This const will take true if there's a device detected that is not a desktop
    this.isMobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    this.currentYear = new Date().getFullYear();
    this.theme.IsLoginPageActive();
    this.updatePageFlags(this.router.url);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updatePageFlags(event.urlAfterRedirects);
      });
    this.initializeLanguageSettings();
  }



  async triggerLanguageChange() {
    // Call the translateTo() method of the translate service
    // and pass the selectedLanguage as an argument
    this.translate.translateTo(this.selectedLanguage);

    // Add the selectedLanguage to the 'language' item in the localStorage
    this.localStorageService.addItem('language', JSON.parse(JSON.stringify(this.selectedLanguage)));

    // Update the initial language to the selectedLanguage
    this.initLang = this.selectedLanguage;

    // Dynamically update the <html> lang attribute
    const htmlElement = document.documentElement; // Access the <html> element
    htmlElement.lang = this.selectedLanguage; // Set the lang attribute

    this.translate.selectLangageChange();
  }

  // Separate function for language initialization
  initializeLanguageSettings() {
    // Retrieve language from localStorage or fallback to a default
    this.selectedLanguage = this.localStorageService.getItem('language') ?? 'fr';
    this.initLang = this.selectedLanguage;

    // Set the <html> lang attribute
    const htmlElement = document.documentElement;
    htmlElement.lang = this.selectedLanguage;

    // Optionally, trigger translation based on the stored language
    this.translate.translateTo(this.selectedLanguage);
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
  }

  selectOption(option: string) {
    this.selectedLanguage = option.toLowerCase();
    this.isOpen = false;
    this.onLanguageChange();
  }

   // Triggered by mouse click
   onMouseInteraction(event: Event): void {
    this.isMouseInteraction = true;
    if (this.isSelectOpen) {
      this.onLanguageChange();
      this.isSelectOpen = false;
    }
    else{
      this.isSelectOpen = true;
    }
  }

  // Triggered by keyboard
  onKeyboardInteraction(event: KeyboardEvent): void {
    this.isMouseInteraction = true;
    if (this.isSelectOpen) {
      if (event.key === 'Enter') {
        this.onLanguageChange();
        this.isSelectOpen = false;
      }
    }
    else{
      if (event.key === 'Enter') {
        this.isSelectOpen = true;
      }
    }
  }

  onLanguageChange(): void {
    if (this.isMouseInteraction) {
      this.triggerLanguageChange();
      this.isMouseInteraction = false;
    }
  }

  private updatePageFlags(url: string): void {
    const path = url.split('?')[0];
    this.isLoginPage = path.startsWith('/login')
      || path.startsWith('/activate')
      || path.startsWith('/sign-up-mfa')
      || path.startsWith('/change-password&redirect=me');

    this.isManagePage = path.startsWith('/manage');
  }
}
